import { createFileRoute } from '@tanstack/react-router'

import type { User } from '@/api/admin-types'
import { useUpdateMe } from '@/api/api-mutations'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'
import type { Locales } from '@/i18n/i18n-types'

export const Route = createFileRoute('/_authenticated/settings/profile/edit')({
	component: RouteComponent,
	staticData: {
		crumb: ({ context: { L } }) => L.General.Edit()
	}
})

function RouteComponent() {
	return (
		<RouteModal asDrawer requiresData loader={async api => await api.getMe({})}>
			{data => <EditProfileForm user={data} />}
		</RouteModal>
	)
}

function EditProfileForm({ user }: { user: User }) {
	const { L, setLocale } = useI18n()
	const { handleSuccess } = useRouteModal()
	const updateMe = useUpdateMe()

	const form = useAppForm({
		defaultValues: {
			firstName: user.firstName || '',
			lastName: user.lastName || '',
			locale: user.locale || 'en'
		},
		onSubmit: async ({ value }) => {
			try {
				await updateMe({
					body: {
						firstName: value.firstName,
						lastName: value.lastName,
						locale: value.locale
					}
				})
				// Update the locale in the client
				if (value.locale) {
					setLocale(value.locale as Locales)
				}
				toast.success(L.Settings.Profile.UpdateSuccess())
				handleSuccess()
			} catch (error: any) {
				toast.error(error.message || 'Failed to update profile')
			}
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Settings.Profile.EditTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='firstName'
							children={field => <field.TextField label={L.Fields.FirstName()} placeholder='John' />}
						/>
						<form.AppField
							name='lastName'
							children={field => <field.TextField label={L.Fields.LastName()} placeholder='Doe' />}
						/>
					</FormFieldGroup>
					<FormFieldGroup>
						<form.AppField
							name='locale'
							children={field => (
								<field.SelectField
									label={L.Fields.Language()}
									options={[
										{ value: 'en', label: 'English' },
										{ value: 'is', label: 'Íslenska' },
										{ value: 'nb', label: 'Norsk Bokmål' },
										{ value: 'da', label: 'Dansk' },
										{ value: 'sv', label: 'Svenska' }
									]}
								/>
							)}
						/>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.SaveButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
